import { c as _c } from "react/compiler-runtime";
import { cn } from "../../lib/utils";
import { Avatar } from "../Avatar/Avatar";
import { Tooltip } from "../Tooltip/Tooltip";
import type { AvatarGroupProps } from "./AvatarGroup.types";
const SIZE_TO_PX = {
  sm: "size-8",
  md: "size-10",
  lg: "size-14"
} as const;
const SIZE_TO_OVERFLOW_TEXT = {
  sm: "text-tiny",
  md: "text-small",
  lg: "text-medium"
} as const;

/**
 * Stack of avatars with overlap, hover-spread, and an overflow indicator.
 *
 * - **Ordering**: items earlier in the array are more prominent (top of stack).
 *   The overflow indicator (if any) sits at the very top.
 * - **Stacking**: avatars overlap tightly by default and spread on hover so each
 *   one is individually legible.
 * - **Z-index**: explicit per child because the underlying `Avatar` creates its
 *   own stacking context — natural DOM paint order alone doesn't reliably keep
 *   later siblings on top of earlier ones.
 */
export const AvatarGroup = t0 => {
  const $ = _c(25);
  const {
    items,
    max: t1,
    renderTrailing,
    size: t2,
    showTooltips: t3,
    className
  } = t0;
  const max = t1 === undefined ? 3 : t1;
  const size = t2 === undefined ? "sm" : t2;
  const showTooltips = t3 === undefined ? false : t3;
  if (items.length === 0) {
    return null;
  }
  let t4;
  let t5;
  let t6;
  if ($[0] !== className || $[1] !== items || $[2] !== max || $[3] !== renderTrailing || $[4] !== showTooltips || $[5] !== size) {
    const visible = items.slice(0, max);
    const overflow = items.slice(max);
    const overflowCount = overflow.length;
    let t7;
    if ($[9] !== overflowCount || $[10] !== size) {
      t7 = overflowCount > 0 ? <span className={cn("flex h-full w-full items-center justify-center rounded-full border-2 border-background bg-default-200 font-medium text-default-700", SIZE_TO_OVERFLOW_TEXT[size])}>{`+${overflowCount}`}</span> : null;
      $[9] = overflowCount;
      $[10] = size;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    const defaultOverflowContent = t7;
    let t8;
    if ($[12] !== defaultOverflowContent || $[13] !== overflowCount || $[14] !== renderTrailing) {
      t8 = renderTrailing ? renderTrailing(overflowCount) : defaultOverflowContent;
      $[12] = defaultOverflowContent;
      $[13] = overflowCount;
      $[14] = renderTrailing;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    const trailingContent = t8;
    const showTrailing = trailingContent !== null && trailingContent !== false && trailingContent !== undefined;
    const tooltipOnOverflow = showTooltips && !renderTrailing && overflowCount > 0;
    const renderedVisible = [...visible].reverse();
    if ($[16] !== className) {
      t4 = cn("-space-x-2.5 hover:-space-x-1 flex shrink-0 items-center", className);
      $[16] = className;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    let t9;
    if ($[18] !== showTooltips || $[19] !== size) {
      t9 = (item, i) => showTooltips ? <Tooltip key={item.id} content={item.tooltip ?? item.name} placement="bottom" color="foreground"><span className="relative inline-flex transition-[margin] duration-200 ease-out" style={{
          zIndex: i + 1
        }}>{item.icon ?? <Avatar name={item.name} src={item.src ?? undefined} size={size} className="border-2 border-background" />}</span></Tooltip> : item.icon ? <span key={item.id} className="relative inline-flex transition-[margin] duration-200 ease-out" style={{
        zIndex: i + 1
      }}>{item.icon}</span> : <Avatar key={item.id} name={item.name} src={item.src ?? undefined} size={size} className="relative border-2 border-background transition-[margin] duration-200 ease-out" style={{
        zIndex: i + 1
      }} />;
      $[18] = showTooltips;
      $[19] = size;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    t5 = renderedVisible.map(t9);
    t6 = showTrailing && (tooltipOnOverflow ? <Tooltip content={<ul className="flex flex-col gap-0.5">{overflow.map(_temp)}</ul>} placement="bottom" color="foreground"><span style={{
        zIndex: visible.length + 1
      }} className={cn("relative transition-[margin] duration-200 ease-out", SIZE_TO_PX[size])}>{trailingContent}</span></Tooltip> : <span aria-hidden="true" style={{
      zIndex: visible.length + 1
    }} className={cn("relative transition-[margin] duration-200 ease-out", SIZE_TO_PX[size])}>{trailingContent}</span>);
    $[0] = className;
    $[1] = items;
    $[2] = max;
    $[3] = renderTrailing;
    $[4] = showTooltips;
    $[5] = size;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[21] !== t4 || $[22] !== t5 || $[23] !== t6) {
    t7 = <span className={t4}>{t5}{t6}</span>;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
};
function _temp(item_0) {
  return <li key={item_0.id}>{item_0.tooltip ?? item_0.name}</li>;
}